import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { buscarHistoricoDesconto } from '../lib/descontoHistorico';
import { buscarAlteracoesCadastro, juntarAlteracoes, type AlteracaoCadastro } from '../lib/alteracoesCadastro';
import { ucCodigoKeys } from '../lib/ucCodigo';

// Mudanças no cadastro de uma UC (ver src/lib/alteracoesCadastro.ts), mais a data
// do último boleto emitido dela — o que veio depois é novidade para o Faturamento.
// Falha na leitura não trava a tela de cálculo/emissão: fica a lista vazia e o erro.
export function useAlteracoesCadastro(
    ucId: number | string | null | undefined,
    opcoes?: { faturaId?: number | string | null; ucCodigo?: string | null },
) {
    const faturaId = opcoes?.faturaId ?? null;
    const ucCodigo = opcoes?.ucCodigo ?? null;
    const [alteracoes, setAlteracoes] = useState<AlteracaoCadastro[]>([]);
    const [ultimoBoletoEm, setUltimoBoletoEm] = useState<string | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    // Troca de fatura com uma leitura em voo (fila de cálculo): só a última resposta vale.
    const pedido = useRef(0);

    const recarregar = useCallback(async () => {
        const meu = ++pedido.current;
        if (!ucId) {
            setAlteracoes([]);
            setUltimoBoletoEm(null);
            setErro(null);
            return;
        }
        setCarregando(true);
        try {
            const chaves = ucCodigo ? ucCodigoKeys(ucCodigo) : [];
            const [cadastro, desconto, ultimo] = await Promise.all([
                // Antes da migration a tabela não existe: segue só com o desconto.
                buscarAlteracoesCadastro(supabase, ucId).catch(() => []),
                buscarHistoricoDesconto(supabase, ucId),
                chaves.length
                    ? (() => {
                        let q = supabase
                            .from('faturas')
                            .select('emitido_em')
                            .in('uc_codigo_norm', chaves)
                            .not('emitido_em', 'is', null);
                        if (faturaId != null) q = q.neq('id', faturaId);
                        return q.order('emitido_em', { ascending: false }).limit(1);
                    })()
                    : Promise.resolve({ data: [] as { emitido_em: string }[] }),
            ]);
            if (meu !== pedido.current) return;
            setAlteracoes(juntarAlteracoes(cadastro, desconto, faturaId));
            setUltimoBoletoEm(((ultimo as any)?.data?.[0]?.emitido_em as string) ?? null);
            setErro(null);
        } catch (e: any) {
            if (meu !== pedido.current) return;
            setAlteracoes([]);
            setErro(e?.message || 'Não foi possível ler as mudanças do cadastro.');
        } finally {
            if (meu === pedido.current) setCarregando(false);
        }
    }, [ucId, faturaId, ucCodigo]);

    useEffect(() => { recarregar(); }, [recarregar]);

    return { alteracoes, ultimoBoletoEm, carregando, erro, recarregar };
}
